import { DataSource } from '@angular/cdk/table';
import { Component, Input } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import * as Papa from 'papaparse';
import { BehaviorSubject ,  Observable } from 'rxjs';

export interface PreviewColumnConfig {
  property: string;
  header: string;
  unique: boolean;
  default: any;
  role: boolean;
}

@Component({
  selector: 'ca-csv-preview',
  templateUrl: 'csv-preview.component.html',
  styleUrls: ['csv-preview.component.scss']
})
export class CsvPreviewComponent {
  protected _file: any;
  displayedColumns: BehaviorSubject<string[]> = new BehaviorSubject([]);
  dataSource: CsvDataSource;
  rows: FormArray;
  data = new BehaviorSubject([]);

  @Input() config: PreviewColumnConfig[];
  @Input() form: FormGroup;
  @Input()
  set results(value: any[]) {
    this._results = value;
    if (value) {
      this.form.disable();
    }
  }
  get results() {
    return this._results;
  }
  @Input() error: any;
  @Input()
  set loading(loading: boolean) {
    if (loading) {
      this.form.disable();
    } else if (!this.results) {
      this.form.enable();
    }
    this._loading = loading;
  }
  get loading() {
    return this._loading;
  }

  @Input()
  set file(value) {
    if (value) {
      Papa.parse(value, {
        header: true,
        complete: this.initTable.bind(this),
        skipEmptyLines: true
      });
    } else {
      this.initTable(null);
    }
  }

  private _loading: boolean;
  private _results: any[];

  initTable(result) {
    this.displayedColumns.next(this.config.map(col => col.property).concat(['action', 'result']));

    // Filter out rows that have duplicates of unique keys
    this.config.forEach(option => {
      let existing = new Set();
      if (option.unique) {
        result.data = result.data.filter(item => {
          if (existing.has(item[option.property])) {
            return false;
          }
          existing.add(item[option.property]);
          return true;
        });
      }
      if (option.default) {
        result.data.forEach(
          item => (item[option.property] = item[option.property] || option.default)
        );
      }
    });

    this.rows = new FormArray(
      result.data.map(row => {
        let fg = new FormGroup({});
        this.config.forEach(option => {
          fg.addControl(option.property, new FormControl(row[option.property]));
        });
        return fg;
      }),
      Validators.minLength(1)
    );

    this.form.setControl('rows', this.rows);
    this.data.next(result.data);
    this.dataSource = new CsvDataSource(this.data.asObservable());
  }

  controlForCell(index: number, property: string) {
    try {
      let item: any = this.rows.controls[index];
      return item.controls[property];
    } catch (ex) {
      return new FormControl('');
    }
  }

  removeAt(idx: number) {
    this.rows.removeAt(idx);
    let items = this.data.value;
    this.data.next(items.slice(0, idx).concat(items.slice(idx + 1)));
  }

  flattenError(result: any) {
    if (result.errors) {
      const errors = result.errors;
      try {
        for (const key in errors) {
          if (Array.isArray(errors[key])) {
            return `Failed (${this.titleCase(key)}: ${errors[key][0]})`;
          }
        }
      } catch (ex) {}
    }
    return 'Import failed.';
  }

  titleCase(value: string) {
    return (
      value[0].toUpperCase() +
      value
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .slice(1)
    );
  }
}

export class CsvDataSource extends DataSource<any> {
  constructor(public data: Observable<any[]>) {
    super();
  }

  connect(): Observable<any[]> {
    return this.data;
  }

  disconnect() {
    // lint
  }
}
